import { createFileRoute, Link } from "@tanstack/react-router";
import { clientCategoryLabel, groupClientsByCategory } from "@/lib/cockpit-areas";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listClients, listEntitlements } from "@/lib/customers.functions";
import { listStaffDirectory } from "@/lib/staff.functions";
import { listOpenCredentialRequestClients } from "@/lib/credential-handover-requests.functions";
import { useAdminSession } from "@/hooks/use-admin-session";
import { warmAdminQuery } from "@/lib/admin-loader";

import {
  MANDATE_TYPES,
  MANDATE_TYPE_LABELS,
  ELECTION_OUTCOMES,
  ELECTION_OUTCOME_LABELS,
  mandateTypeLabel,
  electionOutcomeLabel,
  formatDateDe,
  type MandateType,
  type ElectionOutcome,
} from "@/lib/client-mandate";
import { Building2, Plus, Search, Users } from "lucide-react";

export const Route = createFileRoute("/_admin/admin/clients/")({
  // Ticket AD4a: Sprungziele aus dem Arbeitsvorrat.
  validateSearch: (search: Record<string, unknown>): { outcome?: string; wahl?: "vergangen"; zugangsdaten?: "offen" } => ({
    outcome: typeof search.outcome === "string" ? search.outcome : undefined,
    wahl: search.wahl === "vergangen" ? ("vergangen" as const) : undefined,
    zugangsdaten: search.zugangsdaten === "offen" ? ("offen" as const) : undefined,
  }),
  loader: ({ context }) => {
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin", "clients"],
      queryFn: () => listClients(),
    });
    warmAdminQuery(context.queryClient, {
      queryKey: ["admin", "entitlements", "all"],
      queryFn: () => listEntitlements({ data: {} }),
    });
  },
  component: ClientsPage,
});


type StatusFilter = "active" | "archived" | "inactive" | "all";
// Ticket Z1: Mandatstyp und Wahlausgang als sichtbare und filterbare Spalten.
type MandateFilter = "all" | "none" | MandateType;
type OutcomeFilter = "all" | ElectionOutcome;

// Ticket „Kunden optisch getrennt": Kopfzeile je Kundenkategorie in der
// Kategorie-Farbe. Unbekannte Kategorien fallen auf den neutralen Stil
// zurück und tragen die Fehlerkennzeichnung der zentralen Labels.
const CATEGORY_HEADER_STYLES: Record<string, string> = {
  agenturkunde: "bg-brand-client-agenturkunde text-brand-client-agenturkunde-foreground",
  kurskunde: "bg-brand-client-kurskunde text-brand-client-kurskunde-foreground",
  interessent: "bg-brand-client-interessent text-brand-client-interessent-foreground",
};

function ClientsPage() {
  const routeSearch = Route.useSearch();
  const session = useAdminSession();
  const listClientsFn = useServerFn(listClients);
  const listStaffFn = useServerFn(listStaffDirectory);
  const listEntFn = useServerFn(listEntitlements);

  // Kein `enabled` auf die Rollenabfrage: der Zugang ist im Gruppen-Guard
  // geprüft, die Serverfunktionen setzen die Rechte durch. So startet die
  // Liste sofort statt erst nach der Rollenprüfung.
  const clientsQ = useQuery({
    queryKey: ["admin", "clients"],
    queryFn: () => listClientsFn(),
  });
  // Ansprechpartner-Namen: Administratoren und Editoren lesen dieselbe
  // schlanke Namensliste (nur Name und Funktion).
  const staffQ = useQuery({
    queryKey: ["admin", "staff-directory"],
    queryFn: () => listStaffFn(),
    enabled: session.isAdmin || session.isEditor,
  });
  const entQ = useQuery({
    queryKey: ["admin", "entitlements", "all"],
    queryFn: () => listEntFn({ data: {} }),

  });

  const [q, setQ] = useState("");
  // Ticket SK11: Testdaten sind standardmäßig ausgeblendet.
  const [showTest, setShowTest] = useState(false);
  const [status, setStatus] = useState<StatusFilter>("active");
  // Ticket Z1: Mandatstyp und Wahlausgang filterbar.
  const [mandate, setMandate] = useState<MandateFilter>("all");
  const [outcome, setOutcome] = useState<OutcomeFilter>(
    (ELECTION_OUTCOMES as readonly string[]).includes(routeSearch.outcome ?? "")
      ? (routeSearch.outcome as ElectionOutcome)
      : "all",
  );
  // Wahltag liegt in der Vergangenheit (Sprungziel „Wahlausgang fehlt").
  const pastElectionOnly = routeSearch.wahl === "vergangen";
  const openCredentialsOnly = routeSearch.zugangsdaten === "offen";

  const openCredsFn = useServerFn(listOpenCredentialRequestClients);
  const openCredsQ = useQuery({
    queryKey: ["admin", "open-credential-request-clients"],
    queryFn: () => openCredsFn(),
    enabled: openCredentialsOnly,
    staleTime: 30_000,
  });
  const openCredClientIds = useMemo(
    () => new Set(openCredsQ.data?.clientIds ?? []),
    [openCredsQ.data],
  );

  const staffMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of staffQ.data?.staff ?? []) {
      m.set(s.id, s.name);
    }
    return m;
  }, [staffQ.data]);

  const userCounts = useMemo(() => {
    const m = new Map<string, number>();
    const now = Date.now();
    for (const e of entQ.data ?? []) {
      if (!e.client_id || !e.active) continue;
      if (e.expires_at && new Date(e.expires_at).getTime() <= now) continue;
      // Zähle eindeutige Nutzer pro Kunde
      const key = e.client_id;
      const setKey = `${key}::${e.user_id}`;
      if (!(m as any).__seen) (m as any).__seen = new Set<string>();
      const seen: Set<string> = (m as any).__seen;
      if (seen.has(setKey)) continue;
      seen.add(setKey);
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [entQ.data]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (clientsQ.data ?? []).filter((c) => {
      if (status !== "all" && c.status !== status) return false;
      // Ticket Z1: Filter für Mandatstyp und Wahlausgang.
      if (mandate === "none" && c.mandate_type) return false;
      if (mandate !== "all" && mandate !== "none" && c.mandate_type !== mandate) return false;
      if (outcome !== "all" && (c.election_outcome ?? "offen") !== outcome) return false;
      if (pastElectionOnly) {
        if (!c.election_day) return false;
        if (c.election_day >= new Date().toISOString().slice(0, 10)) return false;
      }
      if (openCredentialsOnly && !openCredClientIds.has(c.id)) return false;
      if (!showTest && (c as { is_test?: boolean }).is_test === true) return false;
      if (!term) return true;
      return c.organization_name.toLowerCase().includes(term);
    });
  }, [
    clientsQ.data,
    q,
    status,
    mandate,
    outcome,
    pastElectionOnly,
    openCredentialsOnly,
    openCredClientIds,
    showTest,
  ]);
  const grouped = useMemo(() => groupClientsByCategory(rows), [rows]);



  if (session.status === "authenticated" && !session.isAdmin && !session.isEditor) {
    return (
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-extrabold md:text-4xl">Kunden</h1>
        <p className="mt-3 text-sm text-muted-foreground">Keine Berechtigung.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold md:text-4xl">Kunden</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Alle Agenturkunden mit Ansprechpartnern und Nutzerkonten.
          </p>
        </div>
        {session.isAdmin && (
          <Link
            to="/admin/clients/$id"
            params={{ id: "new" }}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-brand-button px-4 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" /> Neuer Kunde
          </Link>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Name"
            className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm outline-none focus:border-foreground"
          />
        </div>
        <div className="flex items-center gap-1 rounded-md border border-border bg-background p-1 text-xs font-semibold">
          {(["active", "archived", "inactive", "all"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={
                "rounded px-3 py-1.5 transition-colors " +
                (status === s
                  ? "bg-secondary text-foreground"
                  : "text-muted-foreground hover:text-foreground")
              }
            >
              {s === "all"
                ? "Alle"
                : s === "active"
                  ? "Aktiv"
                  : s === "archived"
                    ? "Archiviert"
                    : "Inaktiv"}
            </button>
          ))}
        </div>
        {/* Ticket Z1: Filter für Mandatstyp und Wahlausgang. */}
        <select
          value={mandate}
          onChange={(e) => setMandate(e.target.value as MandateFilter)}
          className="h-10 rounded-md border border-border bg-background px-2 text-xs font-semibold outline-none focus:border-foreground"
          aria-label="Mandatstyp filtern"
        >
          <option value="all">Alle Mandatstypen</option>
          <option value="none">Ohne Mandatstyp</option>
          {MANDATE_TYPES.map((m) => (
            <option key={m} value={m}>
              {MANDATE_TYPE_LABELS[m]}
            </option>
          ))}
        </select>
        <select
          value={outcome}
          onChange={(e) => setOutcome(e.target.value as OutcomeFilter)}
          className="h-10 rounded-md border border-border bg-background px-2 text-xs font-semibold outline-none focus:border-foreground"
          aria-label="Wahlausgang filtern"
        >
          <option value="all">Alle Wahlausgänge</option>
          {ELECTION_OUTCOMES.map((o) => (
            <option key={o} value={o}>
              {ELECTION_OUTCOME_LABELS[o]}
            </option>
          ))}
        </select>
        {/* Ticket SK11: Kunden aus dem Testbetrieb der Kaufstrecke. */}
        <label className="flex h-10 items-center gap-2 rounded-md border border-border bg-background px-3 text-xs font-semibold">
          <input
            type="checkbox"
            checked={showTest}
            onChange={(e) => setShowTest(e.target.checked)}
          />
          Testdaten einblenden
        </label>
      </div>

      {/* Ticket „drei eigene Tabellen": Jede Kategorie ist eine eigene Karte
          mit eigenem Rahmen, eigener Kopfzeile und eigener Spaltenüberschrift;
          der Abstand zwischen den Karten trennt die Tabellen sichtbar. */}
      <div className="mt-6 flex flex-col gap-8">
        {clientsQ.isLoading && (
          <div className="overflow-hidden rounded-lg border border-border bg-background">
            <div className="divide-y divide-border">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse bg-secondary/30" />
              ))}
            </div>
          </div>
        )}
        {clientsQ.isError && (
          <div className="rounded-lg border border-border bg-background px-4 py-6 text-sm text-destructive">
            Kunden konnten nicht geladen werden.
          </div>
        )}
        {!clientsQ.isLoading && clientsQ.data && clientsQ.data.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-background px-4 py-10 text-center text-sm text-muted-foreground">
            <Building2 className="h-6 w-6 opacity-50" />
            Noch keine Kunden angelegt.
          </div>
        )}

        {/* Ticket: drei Listen nach clients.category (zentrale Zuordnung). */}
        {/* Kopfzeilen je Kategorie farbig — Farben nur über die semantischen
            Tokens aus src/styles.css (--brand-client-*). */}
        {!clientsQ.isLoading && [...grouped.groups, ...(grouped.unknown.length > 0 ? [{ category: "unbekannt", label: "Unbekannte Kategorie", rows: grouped.unknown }] : [])].map((group) => (
        <section key={group.category} data-category-list={group.category} aria-label={group.label} className="overflow-hidden rounded-lg border border-border bg-background">
          <h2
            className={
              "px-4 py-2.5 font-display text-sm font-semibold tracking-wide " +
              (CATEGORY_HEADER_STYLES[group.category] ?? "bg-secondary/20 text-destructive")
            }
          >
            {group.label} <span className="font-body text-xs opacity-80">({group.rows.length})</span>
          </h2>
          {group.rows.length === 0 && (
            <p className="px-4 py-4 text-sm text-muted-foreground">Keine Einträge</p>
          )}
          <div className="grid grid-cols-[2fr_1fr_.8fr_1.4fr_.6fr] gap-4 border-b border-border bg-secondary/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <div>Organisation</div>
            <div>Mandatstyp</div>
            <div>Wahltag</div>
            <div>Ansprechpartner</div>
            <div className="text-right">Nutzer</div>
          </div>
        <div className="divide-y divide-border">
          {group.rows.map((c) => {
            const manager = c.account_manager_id ? staffMap.get(c.account_manager_id) : null;
            const count = userCounts.get(c.id) ?? 0;
            return (
              <Link
                key={c.id}
                to="/admin/clients/$id"
                params={{ id: c.id }}
                className="grid grid-cols-[2fr_1fr_.8fr_1.4fr_.6fr] items-center gap-4 px-4 py-3 text-sm transition-colors hover:bg-secondary/40"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold text-primary">{c.organization_name}</p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    {(c as { is_test?: boolean }).is_test === true && (
                      <span className="inline-flex items-center rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-semibold text-foreground">
                        Testbetrieb
                      </span>
                    )}
                        {!clientCategoryLabel(c.category).ok && (
                      <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive">
                        Unbekannte Kategorie: {c.category ?? "leer"}
                      </span>
                    )}
                    {c.status !== "active" && (
                      <span
                        className={
                          "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold " +
                          (c.status === "archived"
                            ? "bg-muted text-muted-foreground"
                            : "bg-destructive/10 text-destructive")
                        }
                      >
                        {c.status === "archived" ? "Archiviert" : "Inaktiv"}
                      </span>
                    )}
                  </div>
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {mandateTypeLabel(c.mandate_type)}
                </div>
                <div className="truncate text-xs tabular-nums text-muted-foreground">
                  {formatDateDe(c.election_day)}
                </div>
                <div className="truncate text-muted-foreground">
                  {manager ?? (c.account_manager_id ? "…" : "—")}
                </div>
                <div className="flex items-center justify-end gap-1.5 text-muted-foreground">
                  <Users className="h-3.5 w-3.5" />
                  <span className="tabular-nums">{count}</span>
                </div>

              </Link>
            );
          })}
        </div>
        </section>
        ))}
      </div>
    </div>
  );
}
